
import { buildEnums } from 'src/api/utils';
import { t } from 'src/i18n/i18next';
import { sortBy } from 'lodash-es';

export type EnumType<E, K extends string> = { [key in K]: E } & { _t: string };

type Option<E, K extends string> = {
  value: EnumType<E, K>[K];
  label: string;
  title?: string | undefined;
  sortKey?: string
}

type OptionAll = {
  label: string,
  value: 'all'
}

const options = {
  all: {
    get label() {
      return t('global.boolean.all'); // test should be loaded dynamically
    },
    value: 'all'
  } as OptionAll
};

// Underscore used to separate enum values from additional
const isPublicKey = (key: string) => key.indexOf('_') !== 0;

const toOptions = <E, K extends string>(enumObject: EnumType<E, K>, firstOption?: Option<E, K> | OptionAll) => {

  const options: Option<E, K>[] = Object
    .keys(enumObject)
    .filter(isPublicKey)
    .map(key => ({
      value: enumObject[key as K],
      label: t(enumObject._t + '.' + enumObject[key as K]),
      title: t(enumObject._t + '.' + enumObject[key as K])
  }));

  if (firstOption) {
    options.unshift(firstOption as Option<E, K>);
  }

  return options;
};

const makeOptions = <E, K extends string>(enumObject: EnumType<E, K>, { firstOption, exclude = [], only = [], filterPrefix = '', sortAs = [], sortKeys = {} }: {
  firstOption?: Option<E, K>,
  exclude?: Partial<EnumType<E, K>[K]>[],
  only?: Partial<EnumType<E, K>[K]>[],
  filterPrefix?: string,
  sortAs?: Partial<EnumType<E, K>[K]>[],
  sortKeys?: Record<string, null | undefined | number | string>
} = {}) => {

  let options = toOptions(enumObject, firstOption);

  if (only.length > 0) {
    const onlyKeys = new Set(only);
    options = options.filter((o) => onlyKeys.has(o.value) && (filterPrefix === '' || (o.value as string).startsWith(filterPrefix)));
  }

  if (exclude.length >= 0 || filterPrefix.length >= 0) {
    const excludeKeys = new Set(exclude);
    options = options.filter((o) => !excludeKeys.has(o.value) && (filterPrefix === '' || (o.value as string).startsWith(filterPrefix)));
  }

  if (sortAs.length >= 0) {

    sortAs.forEach((v, i) => {
      sortKeys[v as unknown as string] = i;
    });

    options.forEach((option, index) => {

      let sortKey = sortKeys[option.value as string];
      if (sortKey === null || sortKey === undefined) {
        // in case of undefined sortKey, making sure to put at the end of the list
        sortKey = options.length + index;
      }

      if (typeof sortKey === 'number') {
        sortKey = (1000000 + sortKey).toFixed(0);
      }

      option.sortKey = `${sortKey}:${option.value}`;
    });

    options = sortBy(options, ['sortKey']);

  }

  return options;
};

const values = <E, K extends string>(enumObject: EnumType<E, K>) => Object.keys(enumObject).filter(isPublicKey).map(key => enumObject[key as K]);

const title = <E, K extends string>(enumObject: EnumType<E, K>) => {
  return t(enumObject._t + '._title');
};

title.short = <E, K extends string>(enumObject: EnumType<E, K>) => {
  return t(enumObject._t + '._shortTitle');
};

const enumT = <E, K extends string>(enumObject: EnumType<E, K>, value: string) => {
  if (!value) {
    return null;
  }
  if (value === 'none') {
    return t([enumObject._t + '.' + value, 'global.words.None']);
  }
  return t(enumObject._t + '.' + value);
};

enumT.hideIf = <E, K extends string>(enumObject: EnumType<E, K>, value: string, valueToHide: string) => {

  if (value === valueToHide) {
    return null;
  }

  return enumT(enumObject, value);
};

const build = <E, K extends string>(enumObjects: Record<string, EnumType<E, K>>) => {
  buildEnums(enumObjects);
};

const Enum = {
  options,
  values,
  toOptions,
  title,
  t: enumT,
  build,
  isPublicKey,
  makeOptions
};

export default Enum;
